import { HttpClient, HttpErrorResponse, HttpParams, HttpUrlEncodingCodec } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { environment } from '@y42/environment';
import { ErrorHandlerService } from '@y42/error-handling';
import { LineageSettings } from '@y42/json-schema';
import { ORG_API_URL_PARAM } from '@y42/model';
import { map, Observable, ObservableInput, ObservedValueOf, OperatorFunction, throwError } from 'rxjs';
import { LineageErrorResponseHttpModel, UpstreamDownstreamDependency } from './lineage-api.model';

interface LineageUpstreamDownstreamDependenciesResponse {
  upstreamNodes: UpstreamDownstreamDependency[];
  downstreamNodes: UpstreamDownstreamDependency[];
}

interface ManyLineageUpstreamDownstreamDependenciesResponse {
  resultsByKey: { [path: string]: LineageUpstreamDownstreamDependenciesResponse };
}

@Injectable({
  providedIn: 'root',
})
export class LineageApiService {
  constructor(private readonly http: HttpClient, private readonly errorHandlerService: ErrorHandlerService) {}
  private readonly url = `${environment.url.lineage_api}`;
  private readonly codec = new HttpUrlEncodingCodec();

  getLatestLineage(): Observable<LineageSettings> {
    return this.http.get<LineageSettings>(`${this.url}/company/${ORG_API_URL_PARAM}`).pipe(
      map((lineage) => lineage),
      this.handleError('getLatestLineage'),
    );
  }

  getPeakLineage(assetPath: string): Observable<LineageSettings> {
    const params = new HttpParams({
      fromObject: { key: this.codec.encodeKey(assetPath) },
    });

    return this.http.get<LineageSettings>(`${this.url}/company/${ORG_API_URL_PARAM}`, { params }).pipe(
      map((peakLineage) => peakLineage),
      this.handleError('getPeakLineage'),
    );
  }

  getUpstreamAndDownstreamDependencies(assetPath: string): Observable<LineageUpstreamDownstreamDependenciesResponse> {
    const params = new HttpParams({
      fromObject: { key: this.codec.encodeKey(assetPath) },
    });

    return this.http
      .get<LineageUpstreamDownstreamDependenciesResponse>(`${this.url}/company/${ORG_API_URL_PARAM}/direct`, { params })
      .pipe(this.handleError('getUpstreamAndDownstreamDependencies'));
  }

  getManyUpstreamAndDownstreamDependencies(
    assetPaths: string[],
  ): Observable<ManyLineageUpstreamDownstreamDependenciesResponse> {
    const body = {
      keys: assetPaths.map((assetPath) => this.codec.encodeKey(assetPath)),
    };
    return this.http
      .post<ManyLineageUpstreamDownstreamDependenciesResponse>(`${this.url}/company/${ORG_API_URL_PARAM}/direct`, body)
      .pipe(
        this.handleError('getManyUpstreamAndDownstreamDependencies'),
        map((response) => ({
          ...response,
          resultsByKey: Object.keys(response.resultsByKey).reduce((acc, path) => {
            const decodedPath = this.codec.decodeKey(path);
            const result = response.resultsByKey[path];
            if (result) {
              acc[decodedPath] = result;
            }
            return acc;
          }, {} as ManyLineageUpstreamDownstreamDependenciesResponse['resultsByKey']),
        })),
      );
  }

  private handleError<T, O extends ObservableInput<T>>(message: string): OperatorFunction<T, T | ObservedValueOf<O>> {
    return this.errorHandlerService.catchError(`[LineageApiService]: ${message}`, (error: unknown) =>
      throwError(() => {
        if (error instanceof HttpErrorResponse) {
          return new Error(`LineageApi: ${(error.error as LineageErrorResponseHttpModel).error?.message}`);
        }
        return throwError(() => error);
      }),
    );
  }
}
